import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../app/routes';
import { InternalPage, Link } from 'theSource/components';
import { CaretLeftStyled } from '../../../theSource/components/InternalPage/InternalPageStyled';
import {
  useBenefitCategoryQuery,
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
} from '../../queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from '../../../theSource/features/Home/components/EnrollmentBanner/services';
import {
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from '../../../theSource/queries';
import { BenefitDto } from '../../queries/useBenefitCategoryQuery/types';
import { AddYourProviderCard } from './components/AddYourProviderCard';
import { ElectRequestDto } from '../Plans/PlanCard/types';
import { ElectResponseDto } from '../Plans/Volume/types';
import { ErrorResponseDto } from '../../../theSource/types';
import { hasOpenLifeEvent } from '../../utils/common/Utils';
import { queryKeyGenerator, useApiMutation } from '../../../theSource/utils';
import {
  getOptionalPcpPlans,
  getRequiredPcpPlans,
  UPDATE_ELECTION_QUERY_KEY,
} from '../Plans/PlanCard/utils';
import { GenericPagePreview } from '../Shared/PreviewContainers/GenericPagePreview';
import { GenericGroupingContainer } from '../Shared/PreviewContainers/GenericGroupingContainer';
import { PsButtonTuner } from '../../../stencilSetup';

export const AddYourProviders = () => {
  const { t } = useTranslation('pages.enrollmentAddProviders');
  const navigate = useNavigate();

  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  let pcpPlans: BenefitDto[] | undefined = [];

  const { dependentsData, isLoadingDependents } = useEnrollmentFamilyQuery({
    lumosEnabled: true,
    manualEnrollmentContextOverride: enrollmentContext,
  });

  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const subscriberProfile = subscriber?.data?.subscriber_profile;

  const subscribeZipCode = subscriber?.data?.zip_code?.value;

  const { data: benefitData, isLoading: isBenefitsLoading } =
    useBenefitCategoryQuery({
      key: queryKeyGenerator(
        `benefits-details-${enrollmentContext}`,
        orgPlanYearContextId
      ),
      enrollmentContextType: enrollmentContext,
      enabled: true,
      lumosApiHost: String(lumosApiHost),
      includeEligibleDependentsData: true,
      orgPlanYearId: orgPlanYearContextId,
    });

  if (!isBenefitsLoading && !isLoadingConfiguration) {
    pcpPlans = getRequiredPcpPlans(benefitData) || [];
    pcpPlans = [...pcpPlans, ...(getOptionalPcpPlans(benefitData) || [])];
  }
  const handleBackButton = () => {
    window.history.go(-1);
  };

  const electPlan = (params: ElectRequestDto) => {
    updateElection(params);
  };

  const { isLoading: isEditing, mutate: updateElection } = useApiMutation<
    ElectResponseDto,
    ErrorResponseDto,
    ElectRequestDto
  >({
    queryKey: UPDATE_ELECTION_QUERY_KEY,
    queryKeysToInvalidate: queryKeyGenerator(
      `benefits-details-${enrollmentContext}`,
      orgPlanYearContextId
    ),
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/benefits/elect`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const handleClick = () => {
    navigate(`/enrollment/${ROUTES.enrollment.benefitDetails.path}`);
  };

  if (
    isEditing ||
    isBenefitsLoading ||
    isLoadingDependents
  ) {
    return (
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title-loading"
      >
        <GenericGroupingContainer
          id="benefit-loading"
          groupName=""
          loading={true}
          aria-label="Loading..."
        >
          Loading...
        </GenericGroupingContainer>
      </GenericPagePreview>
    );
  }

  return (
    <div
      data-testid="provider-select"
      className="px-4 px-md-0 mt-md-3_5 mt-lg-0 pt-md-4_5 pt-lg-4"
    >
      <div className="back-link-container">
        <Link
          sx={{ mb: '2rem' }}
          internal
          value={t('common:back')}
          underline={false}
          IconComponent={CaretLeftStyled}
          testId="back-button"
          onClick={handleBackButton}
        />
      </div>
      <InternalPage testId="enrollment-providers-details-page">
        <h1>{t('title')}</h1>
        <div>{t('subtitle')}</div>
        <div className="row mx-0 p-0">
          {pcpPlans &&
            pcpPlans?.map((benefit: BenefitDto) => {
              return (
                <AddYourProviderCard
                  key={benefit.benefit.org_benefit_id}
                  benefitData={benefit}
                  subscriber={subscriberProfile}
                  dependents={dependentsData}
                  updateElection={electPlan}
                  subscribeZipCode={subscribeZipCode}
                />
              );
            })}
        </div>
        <div className="py-4 text-left text-md-left">
          {!isBenefitsLoading && (
            <PsButtonTuner
              buttonId="continue-button"
              label={t('continue')}
              level="primary"
              onClick={handleClick}
            />
          )}
        </div>
      </InternalPage>
    </div>
  );
};
